import { zodResolver } from "@hookform/resolvers/zod";
import { Delete as DeleteIcon } from "@mui/icons-material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { useAuthGroupsQuery } from "@/api/hooks/auth-groups-hooks";
import { BaseAutocomplete } from "@/components/a-autocomplete";
import { ModalActionsButtons } from "@/components/modal-actions-buttons";
import { CopyTimingsModal } from "@/module/room-configs-module/timing-config-module/timing-create-edit-modal-components/copy-timings-modal";

// ---------- SCHEMA ----------
const intField = z.preprocess((val) => {
  if (val === "" || val === undefined || val === null) return undefined;
  return val;
}, z.coerce.number().int("Must be a number").min(0, "Must be ≥ 0"));

const minMaxMinPrcMaxPrcSchema = z
  .object({
    min: intField,
    max: intField,
    min_prc: intField,
    max_prc: intField,
  })
  .superRefine((data, ctx) => {
    // Пропускаем если нет значений
    if (
      data.min_prc === undefined ||
      data.max_prc === undefined ||
      data.min === undefined ||
      data.max === undefined
    )
      return;

    if (data.min_prc > data.max_prc) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Min cannot be greater than max",
        path: ["min_prc"],
      });
    }

    if (data.min > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Min cannot be greater than max",
        path: ["min"],
      });
    }
  });

const actionTimingSchema = minMaxMinPrcMaxPrcSchema;

const stageSchema = z.object({
  folds: actionTimingSchema,
  checks: actionTimingSchema,
  bets: actionTimingSchema,
  raises: actionTimingSchema,
  calls: actionTimingSchema,
});

const predictorSchema = actionTimingSchema;

const buyInSchema = z
  .object({
    min: intField,
    max: intField,
  })
  .superRefine((data, ctx) => {
    // Пропускаем если нет значений
    if (data.min === undefined || data.max === undefined) return;

    if (data.min > data.max) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Min cannot be greater than max",
        path: ["min"],
      });
    }
  });

const insuranceSchema = z.object({
  insur_buy: actionTimingSchema,
  insur_cancel: actionTimingSchema,
});

export const TimingSchema = z.object({
  name: z.string().min(1, "Required"),

  auth_groups: z.preprocess((val) => {
    if (Array.isArray(val)) return new Set(val);
    if (val instanceof Set) return val;
    return val;
  }, z.set(z.string()).min(1, "Minimum one group")),

  enabled: z.boolean().default(false),
  data: z.object({
    use_ai_timings: z.boolean(),
    predictor: predictorSchema,
    buyin: buyInSchema,
    ...insuranceSchema.shape,
    preflop: stageSchema,
    flop: stageSchema,
    turn: stageSchema,
    river: stageSchema,
  }),
});

export type TimingForm = z.infer<typeof TimingSchema>;

// ---------- COMPONENT ----------
export function TimingCreateEditModal({
  open,
  mode,
  initialData,
  onSave,
  onCancel,
}: {
  open: boolean;
  mode?: "add" | "edit";
  initialData?: TimingForm;
  onSave?: (data: TimingForm) => void;
  onCancel?: () => void;
}) {
  const { t } = useTranslation();

  const [copyTimingsModalIsOpen, setCopyTimingsModalIsOpen] = useState(false);
  const [copiableTimingName, setCopiableTimingName] = useState<string>("");

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    formState: { errors, isDirty },
  } = useForm<TimingForm>({
    mode: "onChange",
    resolver: zodResolver(TimingSchema),
  });

  useEffect(() => {
    console.log(errors);
  }, [errors]);
  const { data: authGroupsRs, isLoading: authGroupsIsLoading } = useAuthGroupsQuery({
    enabled: open,
  });

  const authGroupsOptions = useMemo(
    () => authGroupsRs?.data.map((it) => it.name).sort((a, b) => a.localeCompare(b)) ?? [],
    [authGroupsRs?.data]
  );

  const defaultActionTiming = () => ({
    min: 0,
    max: 0,
    min_prc: 0,
    max_prc: 0,
  });

  const defaultStage = () => ({
    folds: defaultActionTiming(),
    checks: defaultActionTiming(),
    bets: defaultActionTiming(),
    raises: defaultActionTiming(),
    calls: defaultActionTiming(),
  });

  useEffect(() => {
    reset(
      initialData ?? {
        name: "",
        enabled: true,
        auth_groups: new Set(),
        data: {
          use_ai_timings: true,
          predictor: { min: 0, max: 0, min_prc: 0, max_prc: 0 },
          buyin: { min: 0, max: 0 },
          insur_buy: { min: 0, max: 0, min_prc: 0, max_prc: 0 },
          insur_cancel: { min: 0, max: 0, min_prc: 0, max_prc: 0 },
          preflop: defaultStage(),
          flop: defaultStage(),
          turn: defaultStage(),
          river: defaultStage(),
        },
      }
    );
  }, [open, initialData, reset]);

  const onSubmit: SubmitHandler<TimingForm> = (data) => {
    onSave?.(data);
    onCancel?.();
  };

  // ---------- render helpers ----------
  const renderIntFieldGroup = (base: string) => (
    <Grid container spacing={1}>
      {["min", "max", "min_prc", "max_prc"].map((field) => (
        <Grid item xs={3} key={field}>
          <Controller
            name={`${base}.${field}` as any}
            control={control}
            render={({ field: f, fieldState: { error } }) => (
              <TextField
                {...f}
                type="number"
                label={t(field)}
                placeholder={t(`Enter ${field}`)}
                fullWidth
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
        </Grid>
      ))}
    </Grid>
  );

  const renderStageSection = (label: string, base: string) => (
    <Box
      key={base}
      sx={{
        border: "1px solid #eee",
        borderRadius: 2,
        p: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}
      >
        <Typography variant="subtitle1">{t(label)}</Typography>
        <IconButton
          size="small"
          onClick={() => {
            setCopiableTimingName(label.toLowerCase());
            setCopyTimingsModalIsOpen(true);
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Box>
      {["folds", "checks", "bets", "raises", "calls"].map((action) => (
        <Box
          key={action}
          sx={{
            border: "1px solid #eee",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            {t(action)}
          </Typography>
          {renderIntFieldGroup(`${base}.${action}`)}
        </Box>
      ))}
    </Box>
  );

  // ---------- render ----------
  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      onClose={(event, reason) => {
        if (
          reason === "backdropClick" &&
          isDirty &&
          !window.confirm("Data has been changed. Do you want discard changes?")
        ) {
          return;
        }
        onCancel?.();
      }}
    >
      <DialogTitle>
        {mode === "edit" ? t("edit") : t("createNewTiming")}
        <IconButton onClick={onCancel} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit(onSubmit)}
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            marginTop: 8,
          }}
        >
          {/* === HEADER FIELDS === */}
          <Controller
            name="name"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                label={t("name")}
                fullWidth
                error={!!error}
                helperText={error?.message}
                slotProps={{
                  input: { disabled: mode === "edit" },
                }}
              />
            )}
          />

          <Controller
            name="auth_groups"
            control={control}
            render={({ field: { onChange, value } }) => (
              <BaseAutocomplete
                multiple
                options={authGroupsOptions}
                loading={authGroupsIsLoading}
                value={value ? Array.from(value) : []}
                onChange={(_, newValue) => onChange(new Set(newValue))}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t("authGroups")}
                    fullWidth
                    error={!!errors.auth_groups}
                    helperText={errors.auth_groups?.message}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {authGroupsIsLoading ? <CircularProgress size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}
          />

          <Grid container spacing={2}>
            <Grid item>
              {mode === "add" && (
                <Controller
                  name="enabled"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox {...field} checked={field.value} />}
                      label={t("enabled")}
                    />
                  )}
                />
              )}
            </Grid>
            <Grid item>
              <Controller
                name="data.use_ai_timings"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Checkbox {...field} checked={field.value} />}
                    label={t("presetAiTimings")}
                  />
                )}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* === UPPER BLOCK === */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t("Predictor trigger")}
                </Typography>
                {renderIntFieldGroup("data.predictor")}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 2 }}>
                  {t("Buy-in")}
                </Typography>
                <Grid container spacing={1}>
                  {["min", "max"].map((field) => (
                    <Grid item xs={6} key={field}>
                      <Controller
                        name={`data.buyin.${field}` as any}
                        control={control}
                        render={({ field: f, fieldState: { error } }) => (
                          <TextField
                            {...f}
                            type="number"
                            label={t(field)}
                            fullWidth
                            error={!!error}
                            helperText={error?.message}
                          />
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            </Grid>
          </Grid>

          {/* === INSURANCE BLOCK === */}
          <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t("Insurance")}
            </Typography>
            <Grid container spacing={2}>
              {["insur_buy", "insur_cancel"].map((key) => (
                <Grid item xs={6} key={key}>
                  <Box
                    sx={{
                      border: "1px solid #eee",
                      borderRadius: 2,
                      p: 2,
                    }}
                  >
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {t(key === "insur_buy" ? "Buy" : "Cancel")}
                    </Typography>
                    {renderIntFieldGroup(`data.${key}`)}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* === MAIN TIMINGS === */}
          <Box sx={{ border: "1px solid #eee", borderRadius: 2, p: 2, mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              {t("Main timings")}
            </Typography>
            <Grid container spacing={2}>
              {["preflop", "flop", "turn", "river"].map((stage) => (
                <Grid item xs={4} key={stage}>
                  {renderStageSection(stage.toUpperCase(), `data.${stage}`)}
                </Grid>
              ))}
            </Grid>
          </Box>

          <ModalActionsButtons
            onCancelClick={() => onCancel?.()}
            onConfirmClick={() => void 0}
          />
        </form>

        <CopyTimingsModal
          open={copyTimingsModalIsOpen}
          allAvailableTimings={["preflop", "flop", "turn", "river"]}
          onSave={(addableTimings) => {
            const copiableData = getValues(`data.${copiableTimingName.toLowerCase()}` as any);

            addableTimings.forEach((timing) => {
              setValue(
                `data.${timing}` as any,
                {
                  folds: { ...copiableData.folds },
                  checks: { ...copiableData.checks },
                  bets: { ...copiableData.bets },
                  raises: { ...copiableData.raises },
                  calls: { ...copiableData.calls },
                },
                { shouldDirty: true, shouldTouch: true }
              );
            });

            setCopyTimingsModalIsOpen(false);
          }}
          onCancel={() => setCopyTimingsModalIsOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}
